import { type SearchCombinedConfigPayload } from '@atlassian/search-client/config';
import { generateCombinedConfigHash } from '@atlassian/search-client/search-page/config-query';

import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';

import { type ConnectorRow } from '../thirdPartyPromptTypes.ts';

import { readConnectorRows } from './readConnectorRows.ts';

/** Named so a miss is diagnosable, not silent. */
export type ConnectorRowsFailureReason =
	| 'missing_account_ids'
	| 'cache_entry_missing'
	| 'cache_key_not_found'
	| 'cache_payload_invalid'
	| 'cache_entry_json_parse_failed';

export type ConnectorRowsResult = {
	rows: ConnectorRow[];
	failureReason: ConnectorRowsFailureReason | null;
};

const fail = (failureReason: ConnectorRowsFailureReason): ConnectorRowsResult => ({
	rows: [],
	failureReason,
});

/**
 * The payload can arrive under the GraphQL response envelope or bare. Only `thirdPartyProducts` is
 * checked, because it is the only field read, and `readConnectorRows` validates its contents.
 */
const readCombinedConfig = (result: unknown): SearchCombinedConfigPayload | null => {
	const config =
		(result as { data?: { search?: { configuration?: unknown } } })?.data?.search?.configuration ??
		result;

	return typeof config === 'object' && config !== null && 'thirdPartyProducts' in config
		? (config as SearchCombinedConfigPayload)
		: null;
};

/**
 * Raw connector rows from the cache search writes through Confluence's storage manager. Synchronous
 * and effect free, so server and first client render agree. The cache is written by Rovo and search,
 * not by View Page.
 */
export const readConnectorRowsFromLocalStorage = ({
	userId,
	cloudId,
}: {
	userId: string;
	cloudId: string;
}): ConnectorRowsResult => {
	// Both are nullable and neither can be hashed. Reported, not thrown, so the render survives.
	if (!userId || !cloudId) {
		return fail('missing_account_ids');
	}

	const cached: unknown = confluenceLocalStorageInstance.getItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_SEARCH_COMBINED_CONFIG,
	);

	if (!cached) {
		return fail('cache_entry_missing');
	}

	try {
		// `getItem` parses once. A value that was encoded twice comes back as a string.
		const parsedCache: unknown = typeof cached === 'string' ? JSON.parse(cached) : cached;
		const cacheKey = String(generateCombinedConfigHash(userId, cloudId));

		if (typeof parsedCache !== 'object' || parsedCache === null || !(cacheKey in parsedCache)) {
			return fail('cache_key_not_found');
		}

		const payload = readCombinedConfig(
			(parsedCache as Record<string, { result?: unknown }>)[cacheKey]?.result,
		);

		if (!payload) {
			return fail('cache_payload_invalid');
		}

		return { rows: readConnectorRows(payload), failureReason: null };
	} catch {
		return fail('cache_entry_json_parse_failed');
	}
};
